import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  ClipboardDocumentIcon,
  CurrencyEuroIcon,
  DocumentDuplicateIcon,
  HomeModernIcon,
  IdentificationIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ReferentMutation } from '../../../@types/Company';
import type { Owner } from '../../../@types/Owner';
import type { RootState } from '../../../store/store';
import { getValidReferents } from '../../../utils/branding';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup/Popup.module';
import { ReferentOverrideContext } from '../../common/Variable/Variable.module';
import type { ModelId, VersionId } from '../../Communication/ReferentCampaigns/utils/referentCampaignTemplates';
import {
  extractModelContainerText,
  isCustomModelId,
  renderModelContent,
  tabsConfig,
  versionLabels,
  writeTextToClipboard,
} from '../../Communication/ReferentCampaigns/utils/referentCampaignTemplates';
import type { DpeGeneralMailTemplateKey } from '../../Prospecting/OwnerCommunication/components/DpeGeneralMailings/dpeGeneralMailContent';
import {
  buildDpeGeneralMailingText,
  DPE_GENERAL_MAIL_TEMPLATES,
} from '../../Prospecting/OwnerCommunication/components/DpeGeneralMailings/dpeGeneralMailContent';
import type { EmailContent, FirstContactTemplateType } from '../../Prospecting/OwnerCommunication/utils/emailContentGenerator';
import {
  FIRST_CONTACT_TEMPLATE,
  FIRST_CONTACT_TEMPLATE_LABELS,
  generateConsentAfterCallEmailContent,
  generateDpeEmailContent,
  generateFollowUpEmailContent,
} from '../../Prospecting/OwnerCommunication/utils/emailContentGenerator';

type DpeFirstContactPreview = { kind: 'dpe-first-contact'; templateType: FirstContactTemplateType };
type DpeFollowUpPreview = { kind: 'dpe-follow-up' };
type DpeGeneralMailingPreview = { kind: 'dpe-general-mailing'; templateKey: DpeGeneralMailTemplateKey };
type ConsentAfterCallPreview = { kind: 'consent-after-call' };
type ReferentPreview = {
  kind: 'referent';
  modelId: ModelId;
  versionId: VersionId;
  /**
   * Optional initial referent. When the company has more than one valid
   * referent the modal displays a picker; this value pre-selects which
   * referent's data should be substituted into the rendered template.
   * When omitted, the first valid referent is used by default.
   */
  referent?: ReferentMutation | null;
  /**
   * Filename of the uploaded "Modèle 5 — Personnalisé" document, when the
   * preview targets that custom model. Used to render a placeholder card
   * that surfaces the uploaded file name.
   */
  customDocFilename?: string;
};
type AgReferentPreview = { kind: 'ag-referent'; modelId: ModelId; versionId: VersionId };
type SciPreview = {
  kind: 'sci';
  line: 'rent' | 'transaction';
  id: number;
  titre: string;
  objet: string;
  content: React.ReactNode;
};

export type PreviewTarget =
  | DpeFirstContactPreview
  | DpeFollowUpPreview
  | DpeGeneralMailingPreview
  | ConsentAfterCallPreview
  | ReferentPreview
  | AgReferentPreview
  | SciPreview;

const CONSENT_AFTER_CALL_PREVIEW_COLLABORATOR = 'Susan Blondel';
const CONSENT_AFTER_CALL_PREVIEW_SERVICE: 'transaction' | 'gestion-locative' = 'transaction';
const DPE_GENERAL_MAILING_PREVIEW_RDV_URL = 'https://exemple.declarajuridiques.fr/dpe-general-rdv/exemple';

interface PreviewModalProps {
  isOpen: boolean;
  target: PreviewTarget | null;
  onClose: () => void;
}

const FIRST_CONTACT_ICONS: Record<FirstContactTemplateType, React.ElementType> = {
  [FIRST_CONTACT_TEMPLATE.REFERENT_ONLY]: ChatBubbleBottomCenterTextIcon,
  [FIRST_CONTACT_TEMPLATE.DPE_REPORT]: ChartBarIcon,
  [FIRST_CONTACT_TEMPLATE.COPRO_REPORT]: BuildingOffice2Icon,
  [FIRST_CONTACT_TEMPLATE.BOTH_REPORTS]: DocumentDuplicateIcon,
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V1]: HomeModernIcon,
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V2]: CurrencyEuroIcon,
};

const buildSyntheticOwner = (): Owner => ({
  uuid: 'preview-owner',
  firstname: 'Jean',
  lastname: 'Dupont',
  doorNumber: '12',
  civility: 1,
  step: 'not-contacted',
  dpe: {
    copro: {
      id: 0,
      name: 'Copropriété exemple',
      addresses: [{
        address: '10 rue de la Copropriété',
        postalCode: '75001',
        city: 'Paris',
      }],
    },
  },
});

const referentKey = (referent: ReferentMutation): string =>
  `${referent.firstname ?? ''}|${referent.lastname ?? ''}`;

function PreviewModal({ isOpen, target, onClose }: PreviewModalProps) {
  const company = useSelector((state: RootState) => state.auth.company);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Scoped to the rendered referent / ag-referent body so the copy button
  // reads the substituted text from the modal (and not the duplicated copy
  // that may also be rendered in the underlying page).
  const referentPreviewRef = useRef<HTMLDivElement | null>(null);

  const syntheticOwner = useMemo(() => buildSyntheticOwner(), []);

  const validReferents = useMemo(() => getValidReferents(company ?? null), [company]);

  // Track which referent's data is substituted into the rendered template
  // when previewing a referent-campaign model. Resets whenever the modal is
  // reopened with a different target so the user gets the expected default.
  const [selectedReferentKey, setSelectedReferentKey] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !target || target.kind !== 'referent') {
      setSelectedReferentKey(null);
      return;
    }
    const initial = (target.referent && validReferents.some(r => referentKey(r) === referentKey(target.referent as ReferentMutation)))
      ? target.referent
      : validReferents[0] ?? null;
    setSelectedReferentKey(initial ? referentKey(initial) : null);
  }, [isOpen, target, validReferents]);

  const selectedReferent = useMemo(() => {
    if (!selectedReferentKey) { return null; }
    return validReferents.find(r => referentKey(r) === selectedReferentKey) ?? null;
  }, [selectedReferentKey, validReferents]);

  useEffect(() => {
    if (!isOpen || !target) {
      setEmailSubject('');
      setEmailContent('');
      return;
    }

    if (
      target.kind !== 'dpe-first-contact'
      && target.kind !== 'dpe-follow-up'
      && target.kind !== 'dpe-general-mailing'
      && target.kind !== 'consent-after-call'
    ) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const generate = async() => {
      try {
        let result: EmailContent;
        if (target.kind === 'dpe-follow-up') {
          result = await generateFollowUpEmailContent(syntheticOwner, company ?? null, null, undefined, null);
        } else if (target.kind === 'dpe-first-contact') {
          result = await generateDpeEmailContent(syntheticOwner, company ?? null, null, target.templateType, null);
        } else if (target.kind === 'dpe-general-mailing') {
          const referent = validReferents[0];
          const referentFullName = referent
            ? `${referent.firstname ?? ''} ${referent.lastname ?? ''}`.trim()
            : 'Prénom Nom';
          const syndicName = company?.name?.trim() || 'Votre syndic';
          const payload = buildDpeGeneralMailingText(
            target.templateKey,
            referentFullName,
            syndicName,
            DPE_GENERAL_MAILING_PREVIEW_RDV_URL,
          );
          result = { subject: payload.subject, content: payload.body };
        } else {
          result = generateConsentAfterCallEmailContent(
            syntheticOwner,
            company ?? null,
            CONSENT_AFTER_CALL_PREVIEW_COLLABORATOR,
            CONSENT_AFTER_CALL_PREVIEW_SERVICE,
            new Date(),
            null,
          );
        }
        if (!cancelled) {
          setEmailSubject(result.subject);
          setEmailContent(result.content);
        }
      } catch (error) {
        console.error('PreviewModal - failed to generate email preview:', error);
        if (!cancelled) {
          setEmailSubject('');
          setEmailContent("Impossible de générer l'aperçu de ce modèle.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void generate();

    return () => {
      cancelled = true;
    };
  }, [isOpen, target, syntheticOwner, company, validReferents]);

  if (!target) {
    return null;
  }

  const renderTitle = (): string => {
    if (target.kind === 'dpe-first-contact') {
      return `Aperçu DPE — ${FIRST_CONTACT_TEMPLATE_LABELS[target.templateType]}`;
    }
    if (target.kind === 'dpe-follow-up') {
      return 'Aperçu DPE — Relance après contact initial';
    }
    if (target.kind === 'dpe-general-mailing') {
      return `Aperçu envoi général — ${DPE_GENERAL_MAIL_TEMPLATES[target.templateKey].label}`;
    }
    if (target.kind === 'consent-after-call') {
      return 'Aperçu — Confirmation de transmission de coordonnées';
    }
    if (target.kind === 'referent' || target.kind === 'ag-referent') {
      const tab = tabsConfig.find(t => t.id === target.modelId);
      const version = versionLabels[target.modelId]?.[target.versionId];
      const prefix = target.kind === 'ag-referent' ? 'Aperçu AG' : 'Aperçu Référent';
      return `${prefix} — ${tab?.title ?? target.modelId} · ${version?.label ?? ''}`;
    }
    const lineLabel = target.line === 'rent' ? 'Gestion locative' : 'Transaction';
    return `Aperçu SCI — ${lineLabel} · Modèle ${target.id}`;
  };

  const renderBadge = (): React.ReactNode => {
    if (target.kind === 'dpe-first-contact') {
      const Icon = FIRST_CONTACT_ICONS[target.templateType];
      return (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
          <Icon className="h-4 w-4" />
          <span>{FIRST_CONTACT_TEMPLATE_LABELS[target.templateType]}</span>
        </div>
      );
    }
    if (target.kind === 'dpe-follow-up') {
      return (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
          <ArrowPathIcon className="h-4 w-4" />
          <span>Relance standard — propriétaire certain uniquement</span>
        </div>
      );
    }
    if (target.kind === 'dpe-general-mailing') {
      return (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
          <UserGroupIcon className="h-4 w-4" />
          <span>{DPE_GENERAL_MAIL_TEMPLATES[target.templateKey].label}</span>
        </div>
      );
    }
    if (target.kind === 'consent-after-call') {
      return (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
          <IdentificationIcon className="h-4 w-4" />
          <span>Mise en relation — confirmation après appel téléphonique</span>
        </div>
      );
    }
    if (target.kind === 'ag-referent') {
      return (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          <UserGroupIcon className="h-4 w-4" />
          <span>Stratégie AG · Réutilise le template Référent (Campagne 1)</span>
        </div>
      );
    }
    if (target.kind === 'sci') {
      return (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
          <HomeModernIcon className="h-4 w-4" />
          <span>{target.titre}</span>
        </div>
      );
    }
    return null;
  };

  const renderEmailBody = () => {
    if (isLoading) {
      return (
        <div className="flex h-40 items-center justify-center text-sm text-gray-500">
          Génération de l'aperçu…
        </div>
      );
    }

    const formatted = emailContent.split('\n').map((line, index, array) =>
      `${line}${index < array.length - 1 ? '<br />' : ''}`,
    ).join('');

    return (
      <div className="min-h-0 grow overflow-auto rounded-lg border border-gray-200 bg-white">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-700">À :</span> exemple@copropriete.fr
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-700">Objet :</span> {emailSubject || '—'}
          </p>
        </div>
        <div
          className="px-4 py-3 text-sm leading-relaxed whitespace-pre-line text-gray-800"
          dangerouslySetInnerHTML={{ __html: formatted }}
        />
      </div>
    );
  };

  const renderSciBody = (preview: SciPreview) => (
    <div className="min-h-0 grow overflow-auto rounded-lg border border-gray-200 bg-white">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-gray-700">Objet :</span> {preview.objet}
        </p>
      </div>
      <div className="px-4 py-3 text-sm leading-relaxed text-gray-800">
        {preview.content}
      </div>
    </div>
  );

  const renderReferentPicker = () => {
    if (validReferents.length <= 1) { return null; }
    return (
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
        <span className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
          Référent :
        </span>
        <div className="flex flex-wrap gap-2">
          {validReferents.map((referent) => {
            const key = referentKey(referent);
            const isActive = selectedReferentKey === key;
            const label = `${referent.firstname ?? ''} ${referent.lastname ?? ''}`.trim() || 'Sans nom';
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedReferentKey(key)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                  isActive
                    ? 'border-rgpd-blue bg-rgpd-blue text-white shadow-sm'
                    : 'border-slate-300 bg-white text-slate-600 hover:border-rgpd-blue hover:text-rgpd-blue'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const copyPreview = async() => {
    const container = referentPreviewRef.current;
    const textToCopy = extractModelContainerText(container);
    if (!textToCopy) {
      showToast('error', 'Aucun contenu à copier.');
      return;
    }
    try {
      await writeTextToClipboard(textToCopy);
      showToast('success', 'Contenu copié dans le presse-papiers.');
    } catch (error) {
      console.error('PreviewModal - copy failed:', error);
      showToast('error', 'Impossible de copier le contenu.');
    }
  };

  const renderCopyToolbar = () => (
    <div className="mb-3 flex justify-end">
      <button
        type="button"
        onClick={() => { void copyPreview(); }}
        className="inline-flex items-center gap-2 rounded-lg border border-rgpd-blue px-3 py-1.5 text-xs font-semibold text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
      >
        <ClipboardDocumentIcon className="h-4 w-4" />
        Copier le contenu
      </button>
    </div>
  );

  const renderBody = () => {
    if (target.kind === 'referent') {
      const isCustom = isCustomModelId(target.modelId);
      return (
        <div className="flex min-h-0 grow flex-col">
          {!isCustom && renderReferentPicker()}
          {!isCustom && renderCopyToolbar()}
          <div ref={referentPreviewRef} className="min-h-0 grow overflow-auto">
            <ReferentOverrideContext.Provider value={selectedReferent}>
              {renderModelContent(target.modelId, target.versionId, { filename: target.customDocFilename })}
            </ReferentOverrideContext.Provider>
          </div>
        </div>
      );
    }
    if (target.kind === 'ag-referent') {
      return (
        <div className="flex min-h-0 grow flex-col">
          {renderCopyToolbar()}
          <div ref={referentPreviewRef} className="min-h-0 grow overflow-auto">
            {renderModelContent(target.modelId, target.versionId)}
          </div>
        </div>
      );
    }
    if (target.kind === 'sci') {
      return renderSciBody(target);
    }
    return renderEmailBody();
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      title={renderTitle()}
      width="80%"
      maxWidth="900px"
      height="auto"
      maxHeight="85vh"
    >
      <div className="flex h-full flex-col gap-0">
        {renderBadge()}
        {(target.kind === 'dpe-first-contact' || target.kind === 'dpe-follow-up' || target.kind === 'consent-after-call') && (
          <p className="mb-4 text-xs text-gray-500">
            Aperçu généré sur un propriétaire fictif. Le contenu réel utilise les coordonnées du
            propriétaire et du référent au moment de l'envoi.
          </p>
        )}
        {target.kind === 'dpe-general-mailing' && (
          <p className="mb-4 text-xs text-gray-500">
            Aperçu du mail destiné à tous les copropriétaires. Le lien de rendez-vous et la signature
            du référent sont personnalisés au moment de l&apos;envoi.
          </p>
        )}
        {(target.kind === 'sci') && (
          <p className="mb-4 text-xs text-gray-500">
            Aperçu du modèle SCI tel que présenté sur la page Prospecting › SCIs Référent.
          </p>
        )}
        {renderBody()}
      </div>
    </Popup>
  );
}

export default PreviewModal;
